/**
 * ================================================================
 * CUSTOM HOOK: useMediaQuery
 * ================================================================
 *
 * @hook             useMediaQuery
 * @description      Custom hook for responsive design media query detection
 * @category         UI Hook
 *
 * @parameters
 * - query: string - CSS media query string (e.g., "(max-width: 768px)")
 *
 * @returns
 * - boolean - True if media query matches, false otherwise
 *
 * @sideEffects
 * - Window resize listener: Updates state when viewport changes
 * - Cleanup: Removes event listener on unmount
 *
 * @dependencies
 * - React hooks: useState, useEffect
 * - Window API: matchMedia for media query evaluation
 *
 * @usage
 * ```tsx
 * const isMobile = useMediaQuery('(max-width: 768px)');
 * const isTablet = useMediaQuery('(max-width: 1024px)');
 * const isDesktop = useMediaQuery('(min-width: 1025px)');
 * ```
 * ================================================================
 */

import { useEffect, useState } from 'react';

/**
 * Custom hook for media query detection
 *
 * @param {string} query - CSS media query string
 * @returns {boolean} True if media query matches current viewport
 *
 * @example
 * ```typescript
 * const isMobile = useMediaQuery('(max-width: 768px)');
 *
 * return (
 *   <div>
 *     {isMobile ? <MobileView /> : <DesktopView />}
 *   </div>
 * );
 * ```
 *
 * @since 1.0.0
 */
export function useMediaQuery(query: string): boolean {
  // ================================================================
  // STATE MANAGEMENT
  // ================================================================
  //
  // Initialize with current match state
  // Handle SSR by defaulting to false
  // ================================================================

  const [matches, setMatches] = useState<boolean>(() => {
    // Guard against SSR where window is undefined
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia(query).matches;
  });

  // ================================================================
  // SIDE EFFECTS
  // ================================================================
  //
  // Effect: Set up media query listener
  // Cleanup: Remove listener on unmount or query change
  // Performance: Debounced updates for smooth responsive behavior
  // ================================================================

  useEffect(() => {
    // Guard against SSR
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    /**
     * Handle media query changes
     *
     * @param {MediaQueryListEvent} event - Media query change event
     */
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Set initial state
    setMatches(mediaQuery.matches);

    // Add listener for changes
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup function
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * ================================================================
 * PRESET BREAKPOINTS
 * ================================================================
 *
 * Common responsive breakpoints for convenience
 * Based on Tailwind CSS default breakpoints
 * ================================================================
 */

/**
 * Mobile breakpoint hook (max-width: 640px)
 */
export const useIsMobile = () => useMediaQuery('(max-width: 640px)');

/**
 * Tablet breakpoint hook (max-width: 768px)
 */
export const useIsTablet = () => useMediaQuery('(max-width: 768px)');

/**
 * Desktop breakpoint hook (min-width: 1024px)
 */
export const useIsDesktop = () => useMediaQuery('(min-width: 1024px)');

/**
 * Large desktop breakpoint hook (min-width: 1280px)
 */
export const useIsLargeDesktop = () => useMediaQuery('(min-width: 1280px)');

/**
 * ================================================================
 * END OF HOOK: useMediaQuery
 * ================================================================
 *
 * @exports          useMediaQuery, useIsMobile, useIsTablet, useIsDesktop, useIsLargeDesktop
 * @dependencies     React hooks, Window matchMedia API
 * @usedBy           Components requiring responsive behavior
 *
 * @performance
 * - Memoization: State updates only on actual media query changes
 * - Event cleanup: Proper listener removal prevents memory leaks
 * - SSR safe: Guards against window undefined errors
 *
 * ================================================================
 */
